import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sparkles, Heart } from "lucide-react";

// Lazy preload HomePage images after landing page has rendered
const preloadHomeImages = () => {
  const srcs = [
    () => import("@/assets/telly-clinical.webp"),
    () => import("@/assets/rabbits-clinical.webp"),
    () => import("@/assets/lemo-clinical.webp"),
    () => import("@/assets/field-demo.jpg"),
    () => import("@/assets/staff-training.webp"),
    () => import("@/assets/mattedoc.png"),
  ];
  srcs.forEach(async (load) => {
    const mod = await load();
    const img = new Image();
    img.src = mod.default;
  });
};

const smooth = [0.22, 1, 0.36, 1] as const;


function BackgroundParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const hues = [199, 160, 48, 210];
    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25 - 0.08,
      r: 1 + Math.random() * 2.5,
      alpha: 0.06 + Math.random() * 0.12,
      hue: hues[Math.floor(Math.random() * hues.length)],
      sat: 60 + Math.random() * 30,
      lit: 55 + Math.random() * 25,
    }));

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.lit}%, ${p.alpha})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none" />;
}

type Phase = "idle" | "drop" | "splash" | "reveal";

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, lang, setLang } = useLanguage();
  const [phase, setPhase] = useState<Phase>("idle");

  // Defer image preloading until after LCP
  useEffect(() => {
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(preloadHomeImages);
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(preloadHomeImages, 2000);
      return () => clearTimeout(id);
    }
  }, []);

  const handleEnter = useCallback(() => {
    if (phase !== "idle") return;
    setPhase("drop");
    // Lemon drops ~350ms, then splash ~250ms, then reveal ~400ms
    setTimeout(() => setPhase("splash"), 350);
    setTimeout(() => setPhase("reveal"), 600);
    setTimeout(() => navigate("/home"), 1100);
  }, [phase, navigate]);

  const seed = useCallback((i: number) => {
    const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  }, []);

  // Splash particles — subtle, clean dots
  const splashParticles = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const angle = (i / 24) * Math.PI * 2 + (seed(i) - 0.5) * 0.4;
      const dist = 60 + seed(i + 10) * 120;
      const size = 3 + seed(i + 20) * 5;
      const hue = 48 + (seed(i + 30) - 0.5) * 12;
      return {
        angle, dist, size, hue,
        lightness: 60 + seed(i + 40) * 20,
        delay: seed(i + 50) * 0.08,
        gravity: 0.3 + seed(i + 60) * 0.5,
      };
    });
  }, [seed]);

  // Juice drops — teardrop-shaped, with gravity
  const juiceDrops = useMemo(() => {
    return Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2 + (seed(i + 200) - 0.5) * 0.5;
      const dist = 80 + seed(i + 210) * 160;
      const w = 5 + seed(i + 220) * 6;
      const h = w * (1.4 + seed(i + 230) * 0.8);
      const hue = 48 + (seed(i + 240) - 0.5) * 10;
      return {
        angle, dist, w, h, hue,
        lightness: 58 + seed(i + 250) * 18,
        delay: 0.02 + seed(i + 260) * 0.1,
        gravity: 0.5 + seed(i + 270) * 0.8,
        rotate: (angle * 180) / Math.PI + 90,
        wobble: (seed(i + 280) - 0.5) * 30,
      };
    });
  }, [seed]);

  const isIdle = phase === "idle";
  const isTransitioning = phase !== "idle";

  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex flex-col items-center justify-center bg-background">
      {/* Background gradient */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "linear-gradient(160deg, hsl(199 89% 96%) 0%, hsl(210 20% 98%) 40%, hsl(160 50% 96%) 100%)" }}
      />
      <BackgroundParticles />

      {/* Circular ripple mask reveal */}
      <AnimatePresence>
        {(phase === "splash" || phase === "reveal") && (
          <motion.div
            className="fixed inset-0 z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
          >
            {/* Ripple ring */}
            <motion.div
              className="absolute pointer-events-none"
              style={{
                top: "50%",
                left: "50%",
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: "2px solid hsl(48 90% 65% / 0.6)",
                transform: "translate(-50%, -50%)",
              }}
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 200, opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
            {/* Second ripple */}
            <motion.div
              className="absolute pointer-events-none"
              style={{
                top: "50%",
                left: "50%",
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: "1.5px solid hsl(48 85% 70% / 0.4)",
                transform: "translate(-50%, -50%)",
              }}
              initial={{ scale: 1, opacity: 0.6 }}
              animate={{ scale: 180, opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-screen reveal overlay */}
      <AnimatePresence>
        {phase === "reveal" && (
          <motion.div
            className="fixed inset-0 z-[60] pointer-events-none bg-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
          />
        )}
      </AnimatePresence>

      {/* Splash particles */}
      <AnimatePresence>
        {(phase === "splash" || phase === "reveal") &&
          splashParticles.map((p, i) => (
            <motion.div
              key={i}
              className="absolute z-[55] pointer-events-none rounded-full"
              style={{
                top: "50%",
                left: "50%",
                width: p.size,
                height: p.size,
                background: `radial-gradient(circle, hsl(${p.hue} 95% ${p.lightness + 15}%) 0%, hsl(${p.hue} 90% ${p.lightness}%) 100%)`,
                boxShadow: `0 0 ${p.size * 2}px hsl(${p.hue} 90% ${p.lightness}% / 0.4)`,
              }}
              initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
              animate={{
                x: Math.cos(p.angle) * p.dist,
                y: Math.sin(p.angle) * p.dist + p.gravity * p.dist * 0.6,
                scale: [0, 1.2, 0],
                opacity: [0, 0.9, 0],
              }}
              transition={{
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
                delay: p.delay,
              }}
            />
          ))}
      </AnimatePresence>

      {/* Juice drops — teardrop shaped */}
      <AnimatePresence>
        {(phase === "splash" || phase === "reveal") &&
          juiceDrops.map((d, i) => (
            <motion.div
              key={`drop-${i}`}
              className="absolute z-[55] pointer-events-none"
              style={{
                top: "50%",
                left: "50%",
                width: d.w,
                height: d.h,
                borderRadius: "50% 50% 50% 50% / 35% 35% 65% 65%",
                background: `radial-gradient(ellipse at 35% 25%, hsl(${d.hue} 100% ${d.lightness + 20}%) 0%, hsl(${d.hue} 95% ${d.lightness + 5}%) 40%, hsl(${d.hue} 90% ${d.lightness}%) 100%)`,
                boxShadow: `0 0 ${d.w * 1.5}px hsl(${d.hue} 95% ${d.lightness}% / 0.45), inset ${d.w * 0.15}px ${d.w * 0.15}px ${d.w * 0.4}px hsl(0 0% 100% / 0.3)`,
              }}
              initial={{ x: 0, y: 0, scale: 0, opacity: 0, rotate: d.rotate }}
              animate={{
                x: Math.cos(d.angle) * d.dist,
                y: Math.sin(d.angle) * d.dist + d.gravity * d.dist * 0.7,
                scale: [0, 1.3, 0.8, 0],
                opacity: [0, 1, 0.7, 0],
                rotate: [d.rotate, d.rotate + d.wobble],
              }}
              transition={{
                duration: 0.7,
                ease: [0.15, 0.85, 0.25, 1],
                delay: d.delay,
              }}
            />
          ))}
      </AnimatePresence>

      {/* ===== HERO LEMON (the star) ===== */}
      <motion.div
        className="absolute z-40 pointer-events-none"
        style={{ top: "50%", left: "50%", x: "-50%", y: "-50%" }}
        initial={false}
        animate={
          phase === "drop"
            ? { y: "0%", scale: 1, opacity: 1 }
            : phase === "splash"
            ? { y: "0%", scale: 1.15, opacity: 1 }
            : phase === "reveal"
            ? { y: "0%", scale: 0.6, opacity: 0, rotate: 25 }
            : { y: "-250%", scale: 0.8, opacity: 0 }
        }
        transition={
          phase === "drop"
            ? { duration: 0.35, ease: [0.55, 0, 0.68, 0.5] } // fast gravity fall
            : phase === "splash"
            ? { duration: 0.15, ease: [0.22, 1, 0.36, 1] }
            : phase === "reveal"
            ? { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
            : { duration: 0 }
        }
      >
        <svg width="120" height="140" viewBox="0 0 130 150" fill="none">
          <ellipse cx="65" cy="18" rx="11" ry="17" fill="hsl(160 84% 39%)" opacity="0.85" transform="rotate(-15 65 18)" />
          <ellipse cx="69" cy="20" rx="9" ry="14" fill="hsl(160 70% 50%)" opacity="0.7" transform="rotate(10 69 20)" />
          <rect x="63" y="6" width="4" height="24" rx="2" fill="hsl(160 40% 35%)" />
          <ellipse cx="65" cy="88" rx="46" ry="54" fill="hsl(48 92% 62%)" />
          <ellipse cx="57" cy="78" rx="30" ry="36" fill="hsl(48 95% 72%)" opacity="0.5" />
          <ellipse cx="50" cy="70" rx="14" ry="16" fill="hsl(48 100% 87%)" opacity="0.45" />
        </svg>
      </motion.div>

      {/* ===== IDLE CONTENT ===== */}
      {/* Language toggle */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: isIdle ? 1 : 0 }}
        transition={{ delay: isIdle ? 1 : 0, duration: 0.3 }}
        onClick={() => setLang(lang === "it" ? "en" : "it")}
        className="absolute top-5 right-5 text-xs font-medium z-20 transition-colors text-muted-foreground hover:text-foreground"
      >
        {lang === "it" ? "EN" : "IT"}
      </motion.button>

      <div className="relative z-10 flex flex-col items-center text-center max-w-2xl w-full px-6">
        {/* Static lemon for idle state */}
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={isTransitioning ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22, delay: 0.05 }}
          className="origin-center"
          style={{
            filter: "drop-shadow(0 0 35px hsl(48 90% 60% / 0.35))",
          }}
        >
          <motion.div
            animate={isIdle ? { y: [0, -8, 0] } : {}}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <svg width="130" height="150" viewBox="0 0 130 150" fill="none">
              <ellipse cx="65" cy="18" rx="11" ry="17" fill="hsl(160 84% 39%)" opacity="0.85" transform="rotate(-15 65 18)" />
              <ellipse cx="69" cy="20" rx="9" ry="14" fill="hsl(160 70% 50%)" opacity="0.7" transform="rotate(10 69 20)" />
              <rect x="63" y="6" width="4" height="24" rx="2" fill="hsl(160 40% 35%)" />
              <ellipse cx="65" cy="88" rx="46" ry="54" fill="hsl(48 92% 62%)" />
              <ellipse cx="57" cy="78" rx="30" ry="36" fill="hsl(48 95% 72%)" opacity="0.5" />
              <ellipse cx="50" cy="70" rx="14" ry="16" fill="hsl(48 100% 87%)" opacity="0.45" />
            </svg>
          </motion.div>
        </motion.div>

        {/* Badges */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isTransitioning ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
          transition={{ delay: isIdle ? 0.15 : 0, duration: 0.35 }}
          className="flex gap-2 mt-6"
        >
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-lemon/15 text-lemon-foreground">
            <Sparkles className="w-2.5 h-2.5" /> Digital Therapeutics
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-primary/10 text-primary">
            <Heart className="w-2.5 h-2.5" /> Immersive Healthcare
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={isTransitioning ? { opacity: 0, y: -30, scale: 0.95 } : { opacity: 1, y: 0 }}
          transition={isIdle ? { duration: 0.5, delay: 0.2 } : { duration: 0.25 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mt-4 relative whitespace-nowrap"
          style={{
            background: "linear-gradient(90deg, hsl(210 25% 15%) 0%, hsl(210 25% 15%) 35%, hsl(199 89% 48%) 50%, hsl(210 25% 15%) 65%, hsl(210 25% 15%) 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: isIdle ? "shimmer 7s ease-in-out infinite" : "none",
          }}
        >
          Lemons in the Room
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: isTransitioning ? 0 : 1 }}
          transition={{ delay: isIdle ? 0.35 : 0, duration: 0.3 }}
          className="mt-2 text-sm text-primary font-medium leading-snug max-w-xs"
        >
          {t(
            "Aiutiamo i pazienti a ridurre dolore, ansia e stress attraverso la realtà virtuale.",
            "We help patients reduce pain, anxiety and stress through virtual reality."
          )}
        </motion.p>

        {/* CTA */}
        <motion.button
          initial={{ opacity: 0, y: 16 }}
          animate={isTransitioning ? { scale: 0.8, opacity: 0 } : { opacity: 1, y: 0 }}
          transition={isIdle ? { duration: 0.4, delay: 0.4 } : { duration: 0.2 }}
          whileHover={isIdle ? { scale: 1.05 } : {}}
          whileTap={isIdle ? { scale: 0.95 } : {}}
          onClick={handleEnter}
          className="mt-8 px-8 py-3.5 rounded-full text-sm font-semibold tracking-wide shadow-lg cursor-pointer text-white"
          style={{
            background: "linear-gradient(90deg, hsl(48 92% 58%), hsl(199 89% 48%), hsl(160 84% 39%), hsl(48 92% 58%))",
            backgroundSize: "300% 100%",
            animation: "gradient-flow 4s ease-in-out infinite",
            boxShadow: "0 8px 30px hsl(48 90% 55% / 0.25), 0 2px 10px hsl(199 89% 48% / 0.15)",
          }}
        >
          🍋 {t("Squeeze to enter", "Squeeze to enter")}
        </motion.button>

        {/* Sub-CTA */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: isTransitioning ? 0 : 1 }}
          transition={{ delay: isIdle ? 0.55 : 0, duration: 0.3 }}
          className="mt-3 text-xs text-muted-foreground tracking-wide"
        >
          {t("Entra nell'esperienza immersiva", "Enter the immersive experience")}
        </motion.p>

        {/* Footer tag */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isIdle ? 1 : 0 }}
          transition={{ delay: isIdle ? 0.8 : 0, duration: 0.3 }}
          className="mt-16"
        >
          <div className="gradient-strip h-0.5 w-12 rounded-full mx-auto mb-3" />
          <p className="text-[10px] font-medium tracking-[0.15em] uppercase text-muted-foreground">
            Digital Therapeutics · Immersive Healthcare
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LandingPage;
